import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { isAxiosError } from 'axios';
import { listWhatsAppConversation, sendWhatsApp } from '../../../api/whatsapp';
import { listLookup } from '../../../api/masterData';

export const WhatsAppTab = ({ leadId }: { leadId: number }) => {
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const conversationQuery = useQuery({
    queryKey: ['whatsapp-conversation', leadId],
    queryFn: () => listWhatsAppConversation(leadId),
  });
  const templatesQuery = useQuery({
    queryKey: ['lookup', '/master-data/whatsapp-templates'],
    queryFn: () => listLookup('/master-data/whatsapp-templates', { activeOnly: true }),
  });

  const sendMutation = useMutation({
    mutationFn: () => sendWhatsApp({ leadId, templateId: templateId || undefined, body: templateId ? undefined : body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversation', leadId] });
      queryClient.invalidateQueries({ queryKey: ['lead-timeline', leadId] });
      setBody('');
      setTemplateId('');
      setError(null);
    },
    onError: (err) =>
      setError(isAxiosError(err) ? (err.response?.data?.message ?? 'Failed to send') : 'Failed to send'),
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Send a WhatsApp message
        </Typography>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            select
            label="Use a template (optional)"
            size="small"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : '')}
          >
            <MenuItem value="">
              <em>Free text</em>
            </MenuItem>
            {templatesQuery.data?.map((t) => (
              <MenuItem key={t.id as number} value={t.id as number}>
                {String(t.name)}
              </MenuItem>
            ))}
          </TextField>
          {!templateId && (
            <TextField
              label="Message"
              multiline
              minRows={2}
              fullWidth
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          )}
          <Button
            variant="contained"
            sx={{ alignSelf: 'flex-start' }}
            disabled={(!templateId && !body.trim()) || sendMutation.isPending}
            onClick={() => sendMutation.mutate()}
          >
            {sendMutation.isPending ? 'Sending…' : 'Send'}
          </Button>
        </Stack>
      </Paper>

      <Stack spacing={1.5}>
        {conversationQuery.data?.map((m) => (
          <Paper
            key={m.id}
            variant="outlined"
            sx={{
              p: 2,
              maxWidth: '75%',
              alignSelf: m.direction === 'OUTBOUND' ? 'flex-end' : 'flex-start',
              bgcolor: m.direction === 'OUTBOUND' ? 'primary.main' : 'background.paper',
              color: m.direction === 'OUTBOUND' ? 'primary.contrastText' : 'text.primary',
            }}
          >
            <Typography variant="body2">{m.body}</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Chip size="small" label={m.status} />
              <Typography variant="caption">{new Date(m.createdAt).toLocaleString()}</Typography>
            </Stack>
          </Paper>
        ))}
        {conversationQuery.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No WhatsApp messages yet.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
